import React, { useState } from 'react';
import { MapPin, Clock, TrendingUp, Fuel, Package, Mountain, Activity, Navigation, Settings, AlertTriangle, Wrench, Zap, Route, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import Tooltip from './Tooltip';

const RouteAnalysisCard = ({ analysisData }) => {
  const { getThemeClasses, theme } = useTheme();
  const themeClasses = getThemeClasses();

  // State for collapsible physics parameters (2 levels: model + parameters)
  const [showSuspensionPhysics, setShowSuspensionPhysics] = useState(false);
  const [showSuspensionPhysicsParams, setShowSuspensionPhysicsParams] = useState(false);
  const [showBrakePhysics, setShowBrakePhysics] = useState(false);
  const [showBrakePhysicsParams, setShowBrakePhysicsParams] = useState(false);
  const [showEnginePhysics, setShowEnginePhysics] = useState(false);
  const [showEnginePhysicsParams, setShowEnginePhysicsParams] = useState(false);
  const [showTransmissionPhysics, setShowTransmissionPhysics] = useState(false);
  const [showTransmissionPhysicsParams, setShowTransmissionPhysicsParams] = useState(false);

  if (!analysisData) {
    return null;
  }

  const { missionDetails, terrainParameters, routeColor, stressLevel } = analysisData;

  // Helper function to safely extract numeric value from component health objects
  const safeNumber = (value, defaultVal = 0) => {
    if (value === null || value === undefined) return defaultVal;
    if (typeof value === 'object' && value !== null) {
      if ('health' in value) return typeof value.health === 'number' ? value.health : defaultVal;
      return defaultVal;
    }
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultVal : parsed;
    }
    return defaultVal;
  };

  // Normalize ALL health-related fields to ensure they are numbers, not objects
  const normalizedTerrainParams = {
    ...terrainParameters,
    suspensionHealth: safeNumber(terrainParameters?.suspensionHealth, 85),
    brakeHealth: safeNumber(terrainParameters?.brakeHealth, 78),
    engineHealth: safeNumber(terrainParameters?.engineHealth, 92),
    transmissionHealth: safeNumber(terrainParameters?.transmissionHealth, 88),
    suspensionWearRate: safeNumber(terrainParameters?.suspensionWearRate, 0),
    brakeWearRate: safeNumber(terrainParameters?.brakeWearRate, 0),
    engineLoadStress: safeNumber(terrainParameters?.engineLoadStress, 0),
    transmissionShiftStress: safeNumber(terrainParameters?.transmissionShiftStress, 0),
    suspensionPostMission: safeNumber(terrainParameters?.suspensionPostMission),
    brakePostMission: safeNumber(terrainParameters?.brakePostMission),
    enginePostMission: safeNumber(terrainParameters?.enginePostMission),
    transmissionPostMission: safeNumber(terrainParameters?.transmissionPostMission),
    // Physics model (defensive - backend may have sent object before fix)
    suspensionPhysicsHealth: safeNumber(terrainParameters?.suspensionPhysics?.currentHealth, 90),
    suspensionPhysicsWearRate: safeNumber(terrainParameters?.suspensionPhysics?.wearRate, 0),
    suspensionPhysicsPostMission: safeNumber(terrainParameters?.suspensionPhysics?.postMissionHealth),
    brakePhysicsHealth: safeNumber(terrainParameters?.brakePhysics?.currentHealth, 85),
    brakePhysicsWearRate: safeNumber(terrainParameters?.brakePhysics?.wearRate, 0),
    brakePhysicsPostMission: safeNumber(terrainParameters?.brakePhysics?.postMissionHealth),
    enginePhysicsHealth: safeNumber(terrainParameters?.enginePhysics?.currentHealth, 92),
    enginePhysicsWearRate: safeNumber(terrainParameters?.enginePhysics?.wearRate, 0),
    enginePhysicsPostMission: safeNumber(terrainParameters?.enginePhysics?.postMissionHealth),
    transmissionPhysicsHealth: safeNumber(terrainParameters?.transmissionPhysics?.currentHealth, 90),
    transmissionPhysicsWearRate: safeNumber(terrainParameters?.transmissionPhysics?.wearRate, 0),
    transmissionPhysicsPostMission: safeNumber(terrainParameters?.transmissionPhysics?.postMissionHealth),
  };

  const savedSettings = localStorage.getItem('fracadapt-settings');
  let isMetric = true; // Default to Metric
  let maintenanceAlertThreshold = 70; // Default maintenance alert threshold
  let criticalHealthThreshold = 50; // Default critical health threshold
  
  if (savedSettings) {
    try {
      const settings = JSON.parse(savedSettings);
      isMetric = settings.general?.unitSystem === 'Metric';
      maintenanceAlertThreshold = settings.fleet?.maintenanceAlertThreshold || 70;
      criticalHealthThreshold = settings.fleet?.criticalHealthThreshold || 50;
    } catch (error) {
      console.warn('Failed to load settings');
    }
  }
  
  const weightUnit = isMetric ? 'kg' : 'lbs';
  const distanceUnit = isMetric ? 'km' : 'mi';
  const speedUnit = isMetric ? 'km/h' : 'mph';
  
  // Helper to convert service intervals
  const convertServiceInterval = (km) => {
    if (!km) return 'N/A';
    if (isMetric) {
      return `${km} km`;
    } else {
      return `${Math.round(km * 0.621371)} mi`;
    }
  };
  
  // Helper functions to parse and convert saved values
  const parseValue = (str) => {
    if (!str || str === 'N/A') return null;
    const match = str.toString().match(/[\d,]+\.?\d*/);
    return match ? parseFloat(match[0].replace(/,/g, '')) : null;
  };
  
  const convertDistance = (value, fromUnit) => {
    if (!value) return value;
    const numValue = parseValue(value);
    if (!numValue) return value;
    
    // If saved in km and need mi
    if (fromUnit === 'km' && !isMetric) {
      return `${(numValue * 0.621371).toFixed(1)} mi`;
    }
    // If saved in mi and need km
    if (fromUnit === 'mi' && isMetric) {
      return `${(numValue / 0.621371).toFixed(1)} km`;
    }
    return value; // Already in correct unit
  };
  
  const convertSpeed = (value, fromUnit) => {
    if (!value) return value;
    const numValue = parseValue(value);
    if (!numValue) return value;
    
    // If saved in km/h and need mph
    if (fromUnit === 'km/h' && !isMetric) {
      return `${Math.round(numValue * 0.621371)} mph`;
    }
    // If saved in mph and need km/h
    if (fromUnit === 'mph' && isMetric) {
      return `${Math.round(numValue / 0.621371)} km/h`;
    }
    return value; // Already in correct unit
  };
  
  const convertWeightValue = (value, fromUnit) => {
    if (!value) return value;
    const numValue = parseValue(value);
    if (!numValue) return value;
    
    // If saved in lbs and need kg
    if (fromUnit === 'lbs' && isMetric) {
      return `${Math.round(numValue * 0.453592).toLocaleString()} kg`;
    }
    // If saved in kg and need lbs
    if (fromUnit === 'kg' && !isMetric) {
      return `${Math.round(numValue / 0.453592).toLocaleString()} lbs`;
    }
    return value; // Already in correct unit
  };
  
  // Helper to convert numeric weight values from lbs to current unit system
  const convertWeight = (value) => {
    if (!value) return 0;
    // Value is in lbs (from database/input)
    // If metric, convert lbs to kg
    // If imperial, keep as lbs
    return isMetric ? Math.round(value * 0.453592) : Math.round(value);
  };
  
  // Helper to convert elevation from meters to current unit system
  const convertElevation = (meters) => {
    if (!meters) return 0;
    // Elevation stored in meters
    // If metric, keep as meters
    // If imperial, convert to feet
    return isMetric ? Math.round(meters) : Math.round(meters * 3.28084);
  };
  
  const elevationUnit = isMetric ? 'm' : 'ft';
  
  // Helper to convert speed ranges
  const convertSpeedRange = (speedStr) => {
    if (!speedStr || !speedStr.includes('km/h')) return speedStr;
    const match = speedStr.match(/(\d+)-(\d+)/);
    if (!match) return speedStr;
    const [_, min, max] = match;
    if (isMetric) return speedStr;
    return `${Math.round(min * 0.621371)}-${Math.round(max * 0.621371)} mph`;
  };
  
  // Detect and convert saved values
  const displayDistance = missionDetails.totalDistance?.includes('km') 
    ? convertDistance(missionDetails.totalDistance, 'km')
    : missionDetails.totalDistance?.includes('mi')
    ? convertDistance(missionDetails.totalDistance, 'mi')
    : missionDetails.totalDistance;
    
  const displaySpeed = missionDetails.avgSpeed?.includes('km/h')
    ? convertSpeed(missionDetails.avgSpeed, 'km/h')
    : missionDetails.avgSpeed?.includes('mph')
    ? convertSpeed(missionDetails.avgSpeed, 'mph')
    : missionDetails.avgSpeed;
    
  const displayCargoWeight = missionDetails.cargoWeight?.includes('lbs')
    ? convertWeightValue(missionDetails.cargoWeight, 'lbs')
    : missionDetails.cargoWeight?.includes('kg')
    ? convertWeightValue(missionDetails.cargoWeight, 'kg')
    : missionDetails.cargoWeight;
  
  // Convert fuel estimate (stored in liters)
  const convertFuel = (value, fromUnit) => {
    if (!value) return value;
    const numValue = parseValue(value);
    if (!numValue) return value;
    
    // If saved in L and need gal
    if (fromUnit === 'L' && !isMetric) {
      return `${(numValue * 0.264172).toFixed(1)} gal`;
    }
    // If saved in gal and need L
    if (fromUnit === 'gal' && isMetric) {
      return `${(numValue / 0.264172).toFixed(1)} L`;
    }
    return value; // Already in correct unit
  };
  
  const displayFuelEstimate = missionDetails.fuelEstimate?.includes('L')
    ? convertFuel(missionDetails.fuelEstimate, 'L')
    : missionDetails.fuelEstimate?.includes('gal')
    ? convertFuel(missionDetails.fuelEstimate, 'gal')
    : missionDetails.fuelEstimate;

  // Theme-aware card backgrounds for better contrast
  const getCardBackground = (colorScheme) => {
    if (theme === 'light') {
      switch (colorScheme) {
        case 'green':
          return 'bg-gradient-to-br from-green-50 to-emerald-100 border border-green-300 shadow-sm';
        case 'orange':
          return 'bg-gradient-to-br from-orange-50 to-red-100 border border-orange-300 shadow-sm';
        case 'purple':
          return 'bg-gradient-to-br from-purple-50 to-violet-100 border border-purple-300 shadow-sm';
        case 'blue':
          return 'bg-gradient-to-br from-blue-50 to-cyan-100 border border-blue-300 shadow-sm';
        case 'red':
          return 'bg-gradient-to-br from-red-50 to-orange-100 border border-red-300 shadow-sm';
        default:
          return 'bg-white border border-gray-300 shadow-sm';
      }
    } else {
      // Dark mode - keep existing gradients
      switch (colorScheme) {
        case 'green':
          return 'bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-700/50';
        case 'orange':
          return 'bg-gradient-to-br from-orange-900/30 to-red-800/20 border border-orange-700/50';
        case 'purple':
          return 'bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-700/50';
        case 'blue':
          return 'bg-gradient-to-br from-blue-900/30 to-cyan-800/20 border border-blue-700/50';
        case 'red':
          return 'bg-gradient-to-br from-red-900/20 to-orange-800/20 border border-red-700/50';
        default:
          return 'bg-black/30 border border-gray-700/50';
      }
    }
  };

  // Theme-aware component card backgrounds
  const getComponentCardBackground = (componentType) => {
    if (theme === 'light') {
      switch (componentType) {
        case 'suspension':
          return 'bg-gradient-to-br from-orange-50 to-amber-100 border border-orange-300 shadow-sm';
        case 'brake':
          return 'bg-gradient-to-br from-red-50 to-rose-100 border border-red-300 shadow-sm';
        case 'engine':
          return 'bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-300 shadow-sm';
        case 'transmission':
          return 'bg-gradient-to-br from-purple-50 to-violet-100 border border-purple-300 shadow-sm';
        default:
          return 'bg-white border border-gray-300 shadow-sm';
      }
    } else {
      // Dark mode - keep existing styles
      return `${themeClasses.secondary} border ${themeClasses.border}`;
    }
  };

  return (
    <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 mt-6 shadow-md`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-blue-600 p-2 rounded-lg">
            <Route className="text-white" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>Mission Analysis</h3>
              <div 
                className="w-4 h-4 rounded-full" 
                style={{ backgroundColor: routeColor }}
              ></div>
              <div className={`text-xs font-bold px-3 py-1 rounded-full ${
                stressLevel === 'high' ? 
                  (theme === 'light' ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-red-500/20 text-red-400 border border-red-500/30') :
                stressLevel === 'medium' ? 
                  (theme === 'light' ? 'bg-yellow-100 text-yellow-700 border border-yellow-300' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30') :
                  (theme === 'light' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-green-500/20 text-green-400 border border-green-500/30')
              }`}>
                {stressLevel.toUpperCase()} STRESS
              </div>
            </div>
          </div>
        </div>
        <div className="bg-green-500/20 border border-green-500/50 px-4 py-2 rounded-lg">
          <p className="text-green-400 font-semibold text-sm flex items-center gap-2">
            <CheckCircle size={16} />
            REAL DATA
          </p>
        </div>
      </div>

      {/* Mission Details Section */}
      <div className="mb-6">
        <h4 className={`text-lg font-semibold ${themeClasses.textPrimary} mb-4 flex items-center gap-2`}>
          <MapPin className="text-blue-400" size={20} />
          Route Summary
        </h4>
        
        {/* Mission Information - Reorganized for better grouping */}
        <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4 mb-4`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            
            {/* Mission Info Group */}
            <div className="flex justify-between items-center">
              <span className={`text-sm ${themeClasses.textMuted}`}>Mission Name:</span>
              <span className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{missionDetails.missionName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${themeClasses.textMuted}`}>Start Date:</span>
              <span className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{missionDetails.startDate}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${themeClasses.textMuted}`}>Duration:</span>
              <span className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{missionDetails.duration}</span>
            </div>
            
            {/* Vehicle Info Group */}
            <div className="flex justify-between items-center">
              <span className={`text-sm ${themeClasses.textMuted}`}>Vehicle:</span>
              <span className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{missionDetails.vehicle}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${themeClasses.textMuted}`}>Year:</span>
              <span className={`text-sm font-semibold ${themeClasses.textPrimary}`}>
                {missionDetails.vehicleYear || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${themeClasses.textMuted}`}>Mileage:</span>
              <span className={`text-sm font-semibold ${themeClasses.textPrimary}`}>
                {missionDetails.vehicleMileage !== null && missionDetails.vehicleMileage !== undefined 
                  ? isMetric 
                    ? `${Math.round(missionDetails.vehicleMileage * 1.60934).toLocaleString()} km`
                    : `${missionDetails.vehicleMileage.toLocaleString()} mi`
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${themeClasses.textMuted}`}>Current Health:</span>
              <span className={`text-sm font-semibold ${missionDetails.vehicleHealth !== null && missionDetails.vehicleHealth !== undefined && missionDetails.vehicleHealth >= 80 ? 'text-green-400' : missionDetails.vehicleHealth !== null && missionDetails.vehicleHealth !== undefined && missionDetails.vehicleHealth >= 60 ? 'text-yellow-400' : missionDetails.vehicleHealth !== null && missionDetails.vehicleHealth !== undefined ? 'text-red-400' : themeClasses.textPrimary}`}>
                {missionDetails.vehicleHealth !== null && missionDetails.vehicleHealth !== undefined ? `${missionDetails.vehicleHealth}%` : 'N/A'}
              </span>
            </div>
            
            {/* Cargo Info Group */}
            <div className="flex justify-between items-center">
              <span className={`text-sm ${themeClasses.textMuted}`}>Cargo Capacity:</span>
              <span className={`text-sm font-semibold ${themeClasses.textPrimary}`}>
                {missionDetails.cargoCapacity !== null && missionDetails.cargoCapacity !== undefined 
                    ? `${convertWeight(missionDetails.cargoCapacity).toLocaleString()} ${weightUnit}` 
                    : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${themeClasses.textMuted}`}>Additional Cargo:</span>
              <span className={`text-sm font-semibold ${themeClasses.textPrimary}`}>
                {missionDetails.additionalCargo !== null && missionDetails.additionalCargo !== undefined && missionDetails.additionalCargo > 0 
                  ? `${convertWeight(missionDetails.additionalCargo).toLocaleString()} ${weightUnit}` 
                  : `0 ${weightUnit}`}
              </span>
            </div>
            
          </div>
        </div>
        
        {/* Route Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="text-green-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Total Distance</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{displayDistance}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="text-yellow-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Estimated Time</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{missionDetails.estimatedTime}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Navigation className="text-cyan-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Avg Speed</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{displaySpeed}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Fuel className="text-orange-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Fuel Estimate</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{displayFuelEstimate}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Package className="text-purple-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Cargo Weight</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{displayCargoWeight}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Activity className="text-blue-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>TTI Score</p>
            </div>
            <p className={`font-bold text-lg ${terrainParameters.tti >= 80 ? 'text-green-400' :
              terrainParameters.tti >= 60 ? 'text-yellow-400' :
                terrainParameters.tti >= 40 ? 'text-orange-400' : 'text-red-400'
              }`}>
              {Math.round(terrainParameters.tti)}/100
            </p>
          </div>
        </div>
      </div>



      {/* Terrain Parameters Section */}
      <div className="mb-6">
        <h4 className={`text-lg font-semibold ${themeClasses.textPrimary} mb-4 flex items-center gap-2`}>
          <Activity className="text-green-400" size={20} />
          Route Terrain Analysis
        </h4>

        {/* Primary Terrain Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Elevation Profile */}
          <div className={`${getCardBackground('green')} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Mountain className="text-green-400" size={18} />
              <h5 className="text-green-300 font-semibold text-sm">Elevation Profile</h5>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Gain:
                  <Tooltip text="Total elevation gained along the route. Calculated by summing all positive elevation changes between consecutive route points from SRTM DEM data." />
                </span>
                <span className="text-green-400 font-bold">{convertElevation(terrainParameters.elevationGain).toLocaleString()}{elevationUnit}</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Loss:
                  <Tooltip text="Total elevation lost along the route. Calculated by summing all negative elevation changes between consecutive route points from SRTM DEM data." />
                </span>
                <span className="text-blue-400 font-bold">{convertElevation(terrainParameters.descent).toLocaleString()}{elevationUnit}</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Max Elevation:
                  <Tooltip text="Highest elevation point along the route path from SRTM 30m DEM. Determines oxygen availability and engine performance at altitude." />
                </span>
                <span className={`${themeClasses.textPrimary} font-bold`}>{convertElevation(terrainParameters.maxElevation).toLocaleString()}{elevationUnit}</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Min Elevation:
                  <Tooltip text="Lowest elevation point along the route path from SRTM 30m DEM. Combined with max elevation, indicates total elevation range of the mission." />
                </span>
                <span className={`${themeClasses.textSecondary} font-bold`}>{convertElevation(terrainParameters.minElevation).toLocaleString()}{elevationUnit}</span>
              </div>
            </div>
          </div>

          {/* Slope Analysis */}
          <div className={`${getCardBackground('orange')} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="text-orange-400" size={18} />
              <h5 className="text-orange-300 font-semibold text-sm">Slope Analysis</h5>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Max Slope:
                  <Tooltip text="Maximum slope angle along the route path, calculated from SRTM 30m DEM using path-based sampling. Uses 95th percentile if outliers detected. HMMWV max capability: 60% (31°)." />
                </span>
                <span className={`font-bold ${terrainParameters.maxSlopeDegrees >= 25 ? 'text-red-400' :
                  terrainParameters.maxSlopeDegrees >= 15 ? 'text-orange-400' :
                    terrainParameters.maxSlopeDegrees >= 8 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                  {terrainParameters.maxSlopeDegrees?.toFixed(1) || Math.round(terrainParameters.maxSlope)}°
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Avg Slope:
                  <Tooltip text="Average slope angle along the route path. Used for AASHTO grade classification (Level: 0-10%, Rolling: 10-18%, Hilly: 18-35%). Path-based calculation for route-specific accuracy." />
                </span>
                <span className="text-yellow-400 font-bold">
                  {terrainParameters.avgSlopeDegrees?.toFixed(1) || terrainParameters.avgSlope}°
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Steep Sections:
                  <Tooltip text="Percentage of route with slopes greater than 15° (27% grade). Higher values indicate more challenging terrain requiring low gear and careful driving." />
                </span>
                <span className="text-orange-400 font-bold">{terrainParameters.steepSections}%</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Grade Class:
                  <Tooltip text="AASHTO terrain classification based on average slope: Level (0-10%), Rolling (10-18%), Hilly (18-35%), Mountainous (35-50%), Extreme (>50%). Uses average slope, not maximum, per AASHTO standards." />
                </span>
                <span className={`font-bold ${
                  terrainParameters.gradeClass?.includes('Extreme') || terrainParameters.maxSlopeDegrees >= 25 ? 'text-red-400' :
                  terrainParameters.gradeClass?.includes('Steep') || terrainParameters.maxSlopeDegrees >= 15 ? 'text-orange-400' :
                  terrainParameters.gradeClass?.includes('Moderate') || terrainParameters.maxSlopeDegrees >= 8 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                  {terrainParameters.gradeClass || 
                    (terrainParameters.maxSlopeDegrees >= 25 ? 'Extreme' :
                     terrainParameters.maxSlopeDegrees >= 15 ? 'Steep' :
                     terrainParameters.maxSlopeDegrees >= 8 ? 'Moderate' : 'Gentle')}
                </span>
              </div>
            </div>
          </div>

          {/* Surface Conditions */}
          <div className={`${getCardBackground('purple')} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Activity className="text-purple-400" size={18} />
              <h5 className="text-purple-300 font-semibold text-sm">Surface Conditions</h5>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Surface Type:
                  <Tooltip text="Terrain surface classification derived from SRTM DEM path-based roughness analysis. Categories: Paved Road (<20), Gravel Road (20-35), Dirt Track (35-50), Mixed Terrain (50-65), Rocky Path (>65). Based on ISTVS standards." />
                </span>
                <span className={`${themeClasses.textPrimary} font-bold`}>{terrainParameters.surfaceType}</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Roughness:
                  <Tooltip text="Surface roughness index (0-100) calculated using path-based standard deviation of elevation in 3×3 DEM windows along the route. Lower values indicate smoother terrain." />
                </span>
                <span className={`font-bold ${terrainParameters.roughnessIndex >= 70 ? 'text-red-400' :
                  terrainParameters.roughnessIndex >= 40 ? 'text-orange-400' : 'text-green-400'
                  }`}>
                  {Math.round(terrainParameters.roughnessIndex)}/100
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Traction Index:
                  <Tooltip text="Scientific traction coefficient (0-100%) based on Bekker/ISTVS methods. Factors: surface type, roughness, moisture (wet=-15%), temperature (cold=-10%), dust level, and vegetation. Higher values = better grip." />
                </span>
                <span className="text-cyan-400 font-bold">{Math.round(terrainParameters.tractionIndex)}%</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Obstacle Density:
                  <Tooltip text="Surface-type-based obstacle density (0-100). Paved: 5-12, Gravel: 18-32, Dirt: 38-52, Rocky: 68-82. Derived from surface classification (30m SRTM cannot detect individual obstacles)." />
                </span>
                <span className="text-yellow-400 font-bold">{Math.round(terrainParameters.obstacleDensity)}/100</span>
              </div>
            </div>
          </div>

          {/* Traversability Index */}
          <div className={`${getCardBackground('blue')} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Navigation className="text-blue-400" size={18} />
              <h5 className="text-blue-300 font-semibold text-sm">Traversability</h5>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  TTI Score:
                  <Tooltip text="Terrain Traversability Index (0-100). Calculated using NASA/JPL and NATO NRMM weighted formula: TTI = (Slope×0.4) + (Roughness×0.3) + (Complexity×0.3). Higher scores indicate easier traversability." />
                </span>
                <span className={`font-bold ${terrainParameters.tti >= 80 ? 'text-green-400' :
                  terrainParameters.tti >= 60 ? 'text-yellow-400' :
                    terrainParameters.tti >= 40 ? 'text-orange-400' : 'text-red-400'
                  }`}>
                  {Math.round(terrainParameters.tti)}/100
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Complexity:
                  <Tooltip text="Terrain complexity score based on geomorphometric analysis. Combines elevation variance (40%) and maximum slope (60%). Low: easy terrain, Moderate: mixed terrain, High: complex terrain requiring expert navigation." />
                </span>
                <span className={`font-bold ${terrainParameters.complexity.score >= 70 ? 'text-red-400' :
                  terrainParameters.complexity.score >= 40 ? 'text-orange-400' : 'text-green-400'
                  }`}>
                  {terrainParameters.complexity.level}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Curvature:
                  <Tooltip text="Terrain curvature from DEM analysis using Zevenbergen-Thorne (1987) method. Measures how much the terrain surface bends. Higher values indicate more curved terrain." />
                </span>
                <span className="text-cyan-400 font-bold">{Math.round(terrainParameters.terrainCurvature || 0)}/100</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                  Risk Level:
                  <Tooltip text="Multi-factor risk assessment combining max slope (40%), roughness (30%), and complexity (30%). Low: <40, Moderate: 40-70, High: >70. Indicates overall terrain hazard level for vehicle operations." />
                </span>
                <span className={`font-bold ${
                  terrainParameters.riskLevel?.level === 'High' ? 'text-red-400' :
                  terrainParameters.riskLevel?.level === 'Moderate' ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                  {terrainParameters.riskLevel?.level || 'Low'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Environmental Conditions & Terrain Classification */}
        <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-6 mb-6`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column: Environmental Conditions */}
            <div>
              <h5 className={`${themeClasses.textPrimary} font-semibold mb-4 flex items-center gap-2`}>
                <Package className="text-yellow-400" size={18} />
                Environmental Conditions
              </h5>

              <div className="space-y-3">
                {/* Date Warning */}
                {terrainParameters.dateWarning && (
                  <div className="py-1">
                    <p className="text-yellow-400 text-xs">
                      ⚠️ {terrainParameters.dateWarning}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>
                    Temperature {terrainParameters.durationDays > 0 && `(${terrainParameters.durationDays}-day forecast)`}:
                  </span>
                  <span className={`${themeClasses.textPrimary} font-semibold text-sm`}>
                    {terrainParameters.temperature || 'N/A'}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Moisture Content:</span>
                  <span className={`font-semibold text-sm ${terrainParameters.moisture === 'Dry' ? 'text-orange-400' :
                    terrainParameters.moisture === 'Wet' ? 'text-blue-400' : 'text-yellow-400'
                    }`}>
                    {terrainParameters.moisture} ({terrainParameters.moisturePercent})
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Vegetation Cover:</span>
                  <span className="text-green-400 font-semibold text-sm">{terrainParameters.vegetationCover}</span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Dust Factor:</span>
                  <span className="text-yellow-400 font-semibold text-sm">{terrainParameters.dustFactor !== null ? `${Math.round(terrainParameters.dustFactor)}/100` : 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Right Column: Terrain Classification */}
            <div>
              <h5 className={`${themeClasses.textPrimary} font-semibold mb-4 flex items-center gap-2`}>
                <Mountain className="text-blue-400" size={18} />
                Terrain Classification
              </h5>

              <div className="space-y-3">
                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Primary Classification:</span>
                  <span className={`${themeClasses.textPrimary} font-semibold text-sm`}>{terrainParameters.terrainClass}</span>
                </div>
                
                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Operational Suitability:</span>
                  <span className={`font-semibold text-sm ${terrainParameters.tti >= 80 ? 'text-green-400' :
                    terrainParameters.tti >= 60 ? 'text-yellow-400' :
                      terrainParameters.tti >= 40 ? 'text-orange-400' : 'text-red-400'
                    }`}>
                    {terrainParameters.tti >= 80 ? 'Excellent' :
                      terrainParameters.tti >= 60 ? 'Good' :
                        terrainParameters.tti >= 40 ? 'Challenging' : 'Difficult'}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Vehicle Stress Level:</span>
                  <span className={`font-semibold text-sm ${terrainParameters.complexity.score >= 70 ? 'text-red-400' :
                    terrainParameters.complexity.score >= 40 ? 'text-orange-400' : 'text-green-400'
                    }`}>
                    {terrainParameters.complexity.level}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Recommended Speed:</span>
                  <span className="text-cyan-400 font-semibold text-sm">
                    {terrainParameters.recommendedSpeed ? convertSpeedRange(terrainParameters.recommendedSpeed) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Predictive Component Wear Analysis */}
        <div className={`${getCardBackground('red')} rounded-lg p-6 mt-6`}>
          <h5 className={`${themeClasses.textPrimary} font-semibold mb-4 flex items-center gap-2`}>
            <Settings className="text-red-400" size={18} />
            Enhanced Predictive Component Analysis
          </h5>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Suspension Analysis */}
            <div className={`${getComponentCardBackground('suspension')} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="text-orange-400" size={16} />
                <h6 className="text-orange-300 font-semibold text-sm">Suspension System</h6>
              </div>

              {/* CALIBRATED MODEL */}
              <div className="space-y-2 mb-3">
                <div className="text-xs font-semibold text-orange-300 mb-2">CALIBRATED MODEL:</div>
                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                  <span className={`font-bold text-xs ${normalizedTerrainParams.suspensionHealth >= 80 ? 'text-green-400' :
                    normalizedTerrainParams.suspensionHealth >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {normalizedTerrainParams.suspensionHealth}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Mission Wear:</span>
                  <span className="text-orange-400 font-bold text-xs">
                    -{normalizedTerrainParams.suspensionWearRate}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Post-Mission:</span>
                  <span className={`font-bold text-xs ${
                    (normalizedTerrainParams.suspensionPostMission || (normalizedTerrainParams.suspensionHealth - normalizedTerrainParams.suspensionWearRate)) >= 70 ? 'text-green-400' :
                    (normalizedTerrainParams.suspensionPostMission || (normalizedTerrainParams.suspensionHealth - normalizedTerrainParams.suspensionWearRate)) >= 50 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {normalizedTerrainParams.suspensionPostMission || Math.max(0, normalizedTerrainParams.suspensionHealth - normalizedTerrainParams.suspensionWearRate)}%
                  </span>
                </div>

                <div className={`mt-2 pt-2 border-t ${themeClasses.border}`}>
                  <div className="flex items-center gap-1">
                    {(normalizedTerrainParams.suspensionHealth - normalizedTerrainParams.suspensionWearRate) < criticalHealthThreshold &&
                      <AlertTriangle className="text-red-400" size={12} />}
                    <span className={`text-xs ${(normalizedTerrainParams.suspensionHealth - normalizedTerrainParams.suspensionWearRate) < criticalHealthThreshold ? 'text-red-400' :
                        (normalizedTerrainParams.suspensionHealth - normalizedTerrainParams.suspensionWearRate) < maintenanceAlertThreshold ? 'text-orange-400' : 'text-green-400'
                      }`}>
                      {(normalizedTerrainParams.suspensionHealth - normalizedTerrainParams.suspensionWearRate) < criticalHealthThreshold ? 'Service required before mission' :
                        (normalizedTerrainParams.suspensionHealth - normalizedTerrainParams.suspensionWearRate) < maintenanceAlertThreshold ? 'Service recommended after mission' : 'Normal operation expected'}
                    </span>
                  </div>
                </div>
              </div>

              {/* PHYSICS MODEL (Collapsible) */}
              {terrainParameters.suspensionPhysics && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowSuspensionPhysics(!showSuspensionPhysics)}
                    className="w-full flex items-center justify-center px-2 py-1 bg-indigo-800/30 hover:bg-indigo-800/50 border border-indigo-600/50 rounded transition-colors text-indigo-300"
                  >
                    {showSuspensionPhysics ? (
                      <ChevronUp size={12} />
                    ) : (
                      <ChevronDown size={12} />
                    )}
                  </button>

                  {showSuspensionPhysics && (
                    <div className="mt-3 p-3 bg-indigo-950/50 border border-indigo-600/30 rounded-lg">
                      <div className="text-xs font-semibold text-indigo-300 mb-2">PHYSICS MODEL (Quarter-Car):</div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                          <span className={`font-bold text-xs ${normalizedTerrainParams.suspensionPhysicsHealth >= 80 ? 'text-green-400' :
                            normalizedTerrainParams.suspensionPhysicsHealth >= 60 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {normalizedTerrainParams.suspensionPhysicsHealth}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                            Fatigue Wear:
                            <Tooltip text="Calculated using Miner's cumulative fatigue rule: D = n/N, where n = stress cycles from terrain, N = cycles to failure." />
                          </span>
                          <span className="text-indigo-400 font-bold text-xs">
                            -{normalizedTerrainParams.suspensionPhysicsWearRate}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs`}>Post-Mission:</span>
                          <span className={`font-bold text-xs ${
                            normalizedTerrainParams.suspensionPhysicsPostMission >= 70 ? 'text-green-400' :
                            normalizedTerrainParams.suspensionPhysicsPostMission >= 50 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {normalizedTerrainParams.suspensionPhysicsPostMission}%
                          </span>
                        </div>

                        {/* Nested: Physics Parameters (Collapsible) */}
                        <div className="mt-3">
                          <button
                            onClick={() => setShowSuspensionPhysicsParams(!showSuspensionPhysicsParams)}
                            className="w-full flex items-center justify-center px-1.5 py-0.5 bg-indigo-900/30 hover:bg-indigo-900/50 border border-indigo-700/50 rounded transition-colors text-indigo-400"
                          >
                            {showSuspensionPhysicsParams ? (
                              <ChevronUp size={10} />
                            ) : (
                              <ChevronDown size={10} />
                            )}
                          </button>

                          {showSuspensionPhysicsParams && (
                            <div className="mt-2 p-3 bg-indigo-900/30 border border-indigo-700/30 rounded">
                              <div className="space-y-1.5">
                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Natural Freq (f_n):
                                    <Tooltip text="ω_n = √(k_s/m_s). Typical military vehicles: 1.0-1.5 Hz." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.suspensionPhysics.physicsParams?.naturalFrequency_Hz} Hz
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Damping Ratio (ζ):
                                    <Tooltip text="ζ = c_s/(2√(k_s×m_s)). Typical range: 0.2-0.4." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.suspensionPhysics.physicsParams?.dampingRatio}
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Transmissibility (T):
                                    <Tooltip text="T = √(1+(2ζr)²)/√((1-r²)²+(2ζr)²). T>1 = amplification." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.suspensionPhysics.physicsParams?.transmissibility}
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Body Accel:
                                    <Tooltip text="a_body = (A_road × ω²)/g × T. Vertical acceleration in g's." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.suspensionPhysics.physicsParams?.bodyAcceleration_g} g
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Stress Cycles:
                                    <Tooltip text="n = (distance × 1000) / λ. Number of load cycles." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.suspensionPhysics.physicsParams?.stressCycles?.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Brakes Analysis */}
            <div className={`${getComponentCardBackground('brake')} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="text-red-400" size={16} />
                <h6 className="text-red-300 font-semibold text-sm">Brake System</h6>
              </div>

              {/* CALIBRATED MODEL */}
              <div className="space-y-2 mb-3">
                <div className="text-xs font-semibold text-red-300 mb-2">CALIBRATED MODEL:</div>
                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                  <span className={`font-bold text-xs ${normalizedTerrainParams.brakeHealth >= 80 ? 'text-green-400' :
                    normalizedTerrainParams.brakeHealth >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {normalizedTerrainParams.brakeHealth}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Mission Wear:</span>
                  <span className="text-red-400 font-bold text-xs">
                    -{normalizedTerrainParams.brakeWearRate}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Post-Mission:</span>
                  <span className={`font-bold text-xs ${
                    (normalizedTerrainParams.brakePostMission || (normalizedTerrainParams.brakeHealth - normalizedTerrainParams.brakeWearRate)) >= 70 ? 'text-green-400' :
                    (normalizedTerrainParams.brakePostMission || (normalizedTerrainParams.brakeHealth - normalizedTerrainParams.brakeWearRate)) >= 50 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {normalizedTerrainParams.brakePostMission || Math.max(0, normalizedTerrainParams.brakeHealth - normalizedTerrainParams.brakeWearRate)}%
                  </span>
                </div>

                <div className={`mt-2 pt-2 border-t ${themeClasses.border}`}>
                  <div className="flex items-center gap-1">
                    {(normalizedTerrainParams.brakeHealth - normalizedTerrainParams.brakeWearRate) < criticalHealthThreshold &&
                      <AlertTriangle className="text-red-400" size={12} />}
                    <span className={`text-xs ${(normalizedTerrainParams.brakeHealth - normalizedTerrainParams.brakeWearRate) < criticalHealthThreshold ? 'text-red-400' :
                        (normalizedTerrainParams.brakeHealth - normalizedTerrainParams.brakeWearRate) < maintenanceAlertThreshold ? 'text-orange-400' : 'text-green-400'
                      }`}>
                      {(normalizedTerrainParams.brakeHealth - normalizedTerrainParams.brakeWearRate) < criticalHealthThreshold ? 'Service required before mission' :
                        (normalizedTerrainParams.brakeHealth - normalizedTerrainParams.brakeWearRate) < maintenanceAlertThreshold ? 'Service recommended after mission' : 'Normal operation expected'}
                    </span>
                  </div>
                </div>
              </div>

              {/* PHYSICS MODEL (Collapsible) */}
              {terrainParameters.brakePhysics && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowBrakePhysics(!showBrakePhysics)}
                    className="w-full flex items-center justify-center px-2 py-1 bg-indigo-800/30 hover:bg-indigo-800/50 border border-indigo-600/50 rounded transition-colors text-indigo-300"
                  >
                    {showBrakePhysics ? (
                      <ChevronUp size={12} />
                    ) : (
                      <ChevronDown size={12} />
                    )}
                  </button>

                  {showBrakePhysics && (
                    <div className="mt-3 p-3 bg-indigo-950/50 border border-indigo-600/30 rounded-lg">
                      <div className="text-xs font-semibold text-indigo-300 mb-2">PHYSICS MODEL (Thermal):</div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                          <span className={`font-bold text-xs ${normalizedTerrainParams.brakePhysicsHealth >= 80 ? 'text-green-400' :
                            normalizedTerrainParams.brakePhysicsHealth >= 60 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {normalizedTerrainParams.brakePhysicsHealth}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                            Thermal Wear:
                            <Tooltip text="W_thermal = k_wear × N_cycles × (ΔT/T_ref)^n. Thermal wear from brake temperature rise." />
                          </span>
                          <span className="text-indigo-400 font-bold text-xs">
                            -{normalizedTerrainParams.brakePhysicsWearRate}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs`}>Post-Mission:</span>
                          <span className={`font-bold text-xs ${
                            normalizedTerrainParams.brakePhysicsPostMission >= 70 ? 'text-green-400' :
                            normalizedTerrainParams.brakePhysicsPostMission >= 50 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {normalizedTerrainParams.brakePhysicsPostMission}%
                          </span>
                        </div>

                        {/* Nested: Physics Parameters (Collapsible) */}
                        <div className="mt-3">
                          <button
                            onClick={() => setShowBrakePhysicsParams(!showBrakePhysicsParams)}
                            className="w-full flex items-center justify-center px-1.5 py-0.5 bg-indigo-900/30 hover:bg-indigo-900/50 border border-indigo-700/50 rounded transition-colors text-indigo-400"
                          >
                            {showBrakePhysicsParams ? (
                              <ChevronUp size={10} />
                            ) : (
                              <ChevronDown size={10} />
                            )}
                          </button>

                          {showBrakePhysicsParams && (
                            <div className="mt-2 p-3 bg-indigo-900/30 border border-indigo-700/30 rounded">
                              <div className="space-y-1.5">
                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    P_braking:
                                    <Tooltip text="P = m×g×|sin(θ)|×v. Power dissipated during braking (W)." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.brakePhysics.physicsParams?.powerDissipation_W?.toLocaleString()} W
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    E_braking:
                                    <Tooltip text="E = m×g×descent. Total energy dissipated (J)." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.brakePhysics.physicsParams?.brakingEnergy_J?.toLocaleString()} J
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    ΔT rise:
                                    <Tooltip text="ΔT = E_braking/(4×m_disc×c_p). Temperature rise (°C)." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.brakePhysics.physicsParams?.temperatureRise_C} °C
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Thermal Cycles:
                                    <Tooltip text="N_cycles: number of brake applications." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.brakePhysics.physicsParams?.thermalCycles}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Engine Analysis */}
            <div className={`${getComponentCardBackground('engine')} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <Zap className="text-blue-400" size={16} />
                <h6 className="text-blue-300 font-semibold text-sm">Engine System</h6>
              </div>

              {/* CALIBRATED MODEL */}
              <div className="space-y-2 mb-3">
                <div className="text-xs font-semibold text-blue-300 mb-2">CALIBRATED MODEL:</div>
                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                  <span className={`font-bold text-xs ${normalizedTerrainParams.engineHealth >= 80 ? 'text-green-400' :
                    normalizedTerrainParams.engineHealth >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {normalizedTerrainParams.engineHealth}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Load Stress:</span>
                  <span className="text-blue-400 font-bold text-xs">
                    +{normalizedTerrainParams.engineLoadStress}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Post-Mission:</span>
                  <span className={`font-bold text-xs ${
                    (normalizedTerrainParams.enginePostMission || Math.round(normalizedTerrainParams.engineHealth - normalizedTerrainParams.engineLoadStress * 0.3)) >= 70 ? 'text-green-400' :
                    (normalizedTerrainParams.enginePostMission || Math.round(normalizedTerrainParams.engineHealth - normalizedTerrainParams.engineLoadStress * 0.3)) >= 50 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {normalizedTerrainParams.enginePostMission || Math.max(0, Math.round(normalizedTerrainParams.engineHealth - normalizedTerrainParams.engineLoadStress * 0.3))}%
                  </span>
                </div>

                <div className={`mt-2 pt-2 border-t ${themeClasses.border}`}>
                  <div className="flex items-center gap-1">
                    {(normalizedTerrainParams.enginePostMission || Math.round(normalizedTerrainParams.engineHealth - normalizedTerrainParams.engineLoadStress * 0.3)) < criticalHealthThreshold &&
                      <AlertTriangle className="text-red-400" size={12} />}
                    <span className={`text-xs ${(normalizedTerrainParams.enginePostMission || Math.round(normalizedTerrainParams.engineHealth - normalizedTerrainParams.engineLoadStress * 0.3)) < criticalHealthThreshold ? 'text-red-400' :
                        (normalizedTerrainParams.enginePostMission || Math.round(normalizedTerrainParams.engineHealth - normalizedTerrainParams.engineLoadStress * 0.3)) < maintenanceAlertThreshold ? 'text-orange-400' : 'text-green-400'
                      }`}>
                      {(normalizedTerrainParams.enginePostMission || Math.round(normalizedTerrainParams.engineHealth - normalizedTerrainParams.engineLoadStress * 0.3)) < criticalHealthThreshold ? 'Service required before mission' :
                        (normalizedTerrainParams.enginePostMission || Math.round(normalizedTerrainParams.engineHealth - normalizedTerrainParams.engineLoadStress * 0.3)) < maintenanceAlertThreshold ? 'Service recommended after mission' : 'Normal operation expected'}
                    </span>
                  </div>
                </div>
              </div>

              {/* PHYSICS MODEL (Collapsible) */}
              {terrainParameters.enginePhysics && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowEnginePhysics(!showEnginePhysics)}
                    className="w-full flex items-center justify-center px-2 py-1 bg-indigo-800/30 hover:bg-indigo-800/50 border border-indigo-600/50 rounded transition-colors text-indigo-300"
                  >
                    {showEnginePhysics ? (
                      <ChevronUp size={12} />
                    ) : (
                      <ChevronDown size={12} />
                    )}
                  </button>

                  {showEnginePhysics && (
                    <div className="mt-3 p-3 bg-indigo-950/50 border border-indigo-600/30 rounded-lg">
                      <div className="text-xs font-semibold text-indigo-300 mb-2">PHYSICS MODEL (Power Demand):</div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                          <span className={`font-bold text-xs ${normalizedTerrainParams.enginePhysicsHealth >= 80 ? 'text-green-400' :
                            normalizedTerrainParams.enginePhysicsHealth >= 60 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {normalizedTerrainParams.enginePhysicsHealth}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                            Load Wear:
                            <Tooltip text="W_engine = k_load × (Load%/100)^n × duration. Wear scales with load squared." />
                          </span>
                          <span className="text-indigo-400 font-bold text-xs">
                            -{normalizedTerrainParams.enginePhysicsWearRate}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs`}>Post-Mission:</span>
                          <span className={`font-bold text-xs ${
                            normalizedTerrainParams.enginePhysicsPostMission >= 70 ? 'text-green-400' :
                            normalizedTerrainParams.enginePhysicsPostMission >= 50 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {normalizedTerrainParams.enginePhysicsPostMission}%
                          </span>
                        </div>

                        {/* Nested: Physics Parameters (Collapsible) */}
                        <div className="mt-3">
                          <button
                            onClick={() => setShowEnginePhysicsParams(!showEnginePhysicsParams)}
                            className="w-full flex items-center justify-center px-1.5 py-0.5 bg-indigo-900/30 hover:bg-indigo-900/50 border border-indigo-700/50 rounded transition-colors text-indigo-400"
                          >
                            {showEnginePhysicsParams ? (
                              <ChevronUp size={10} />
                            ) : (
                              <ChevronDown size={10} />
                            )}
                          </button>

                          {showEnginePhysicsParams && (
                            <div className="mt-2 p-3 bg-indigo-900/30 border border-indigo-700/30 rounded">
                              <div className="space-y-1.5">
                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    P_required:
                                    <Tooltip text="P_rolling + P_grade + P_drag (W). Steady cruise at 60 km/h." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.enginePhysics.physicsParams?.P_required_W?.toLocaleString()} W
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    P_available:
                                    <Tooltip text="P_rated × (1 - h/12,000) × (1 - 0.001×(T-15)). Altitude + temp derating." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.enginePhysics.physicsParams?.P_available_W?.toLocaleString()} W
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Load%:
                                    <Tooltip text="(P_required / P_available) × 100. Engine load percentage." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.enginePhysics.physicsParams?.Load_pct}%
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Duration:
                                    <Tooltip text="duration_h = distance/60. Mission duration in hours." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.enginePhysics.physicsParams?.duration_h} h
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Transmission Analysis */}
            <div className={`${getComponentCardBackground('transmission')} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <Settings className="text-purple-400" size={16} />
                <h6 className="text-purple-300 font-semibold text-sm">Transmission</h6>
              </div>

              {/* CALIBRATED MODEL */}
              <div className="space-y-2 mb-3">
                <div className="text-xs font-semibold text-purple-300 mb-2">CALIBRATED MODEL:</div>
                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                  <span className={`font-bold text-xs ${normalizedTerrainParams.transmissionHealth >= 80 ? 'text-green-400' :
                    normalizedTerrainParams.transmissionHealth >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {normalizedTerrainParams.transmissionHealth}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Shift Stress:</span>
                  <span className="text-purple-400 font-bold text-xs">
                    +{normalizedTerrainParams.transmissionShiftStress}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Post-Mission:</span>
                  <span className={`font-bold text-xs ${
                    (normalizedTerrainParams.transmissionPostMission || Math.round(normalizedTerrainParams.transmissionHealth - normalizedTerrainParams.transmissionShiftStress * 0.2)) >= 70 ? 'text-green-400' :
                    (normalizedTerrainParams.transmissionPostMission || Math.round(normalizedTerrainParams.transmissionHealth - normalizedTerrainParams.transmissionShiftStress * 0.2)) >= 50 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {normalizedTerrainParams.transmissionPostMission || Math.max(0, Math.round(normalizedTerrainParams.transmissionHealth - normalizedTerrainParams.transmissionShiftStress * 0.2))}%
                  </span>
                </div>

                <div className={`mt-2 pt-2 border-t ${themeClasses.border}`}>
                  <div className="flex items-center gap-1">
                    {(normalizedTerrainParams.transmissionPostMission || Math.round(normalizedTerrainParams.transmissionHealth - normalizedTerrainParams.transmissionShiftStress * 0.2)) < criticalHealthThreshold &&
                      <AlertTriangle className="text-red-400" size={12} />}
                    <span className={`text-xs ${(normalizedTerrainParams.transmissionPostMission || Math.round(normalizedTerrainParams.transmissionHealth - normalizedTerrainParams.transmissionShiftStress * 0.2)) < criticalHealthThreshold ? 'text-red-400' :
                        (normalizedTerrainParams.transmissionPostMission || Math.round(normalizedTerrainParams.transmissionHealth - normalizedTerrainParams.transmissionShiftStress * 0.2)) < maintenanceAlertThreshold ? 'text-orange-400' : 'text-green-400'
                      }`}>
                      {(normalizedTerrainParams.transmissionPostMission || Math.round(normalizedTerrainParams.transmissionHealth - normalizedTerrainParams.transmissionShiftStress * 0.2)) < criticalHealthThreshold ? 'Service required before mission' :
                        (normalizedTerrainParams.transmissionPostMission || Math.round(normalizedTerrainParams.transmissionHealth - normalizedTerrainParams.transmissionShiftStress * 0.2)) < maintenanceAlertThreshold ? 'Service recommended after mission' : 'Normal operation expected'}
                    </span>
                  </div>
                </div>
              </div>

              {/* PHYSICS MODEL (Collapsible) */}
              {terrainParameters.transmissionPhysics && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowTransmissionPhysics(!showTransmissionPhysics)}
                    className="w-full flex items-center justify-center px-2 py-1 bg-indigo-800/30 hover:bg-indigo-800/50 border border-indigo-600/50 rounded transition-colors text-indigo-300"
                  >
                    {showTransmissionPhysics ? (
                      <ChevronUp size={12} />
                    ) : (
                      <ChevronDown size={12} />
                    )}
                  </button>

                  {showTransmissionPhysics && (
                    <div className="mt-3 p-3 bg-indigo-950/50 border border-indigo-600/30 rounded-lg">
                      <div className="text-xs font-semibold text-indigo-300 mb-2">PHYSICS MODEL (Shift Frequency):</div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                          <span className={`font-bold text-xs ${normalizedTerrainParams.transmissionPhysicsHealth >= 80 ? 'text-green-400' :
                            normalizedTerrainParams.transmissionPhysicsHealth >= 60 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {normalizedTerrainParams.transmissionPhysicsHealth}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                            Clutch Wear:
                            <Tooltip text="W_clutch = k × N_shifts × (T_transmission/T_rated)^1.5. Energy-based wear from shift frequency." />
                          </span>
                          <span className="text-indigo-400 font-bold text-xs">
                            -{normalizedTerrainParams.transmissionPhysicsWearRate}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className={`${themeClasses.textMuted} text-xs`}>Post-Mission:</span>
                          <span className={`font-bold text-xs ${
                            normalizedTerrainParams.transmissionPhysicsPostMission >= 70 ? 'text-green-400' :
                            normalizedTerrainParams.transmissionPhysicsPostMission >= 50 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {normalizedTerrainParams.transmissionPhysicsPostMission}%
                          </span>
                        </div>

                        {/* Nested: Physics Parameters (Collapsible) */}
                        <div className="mt-3">
                          <button
                            onClick={() => setShowTransmissionPhysicsParams(!showTransmissionPhysicsParams)}
                            className="w-full flex items-center justify-center px-1.5 py-0.5 bg-indigo-900/30 hover:bg-indigo-900/50 border border-indigo-700/50 rounded transition-colors text-indigo-400"
                          >
                            {showTransmissionPhysicsParams ? (
                              <ChevronUp size={10} />
                            ) : (
                              <ChevronDown size={10} />
                            )}
                          </button>

                          {showTransmissionPhysicsParams && (
                            <div className="mt-2 p-3 bg-indigo-900/30 border border-indigo-700/30 rounded">
                              <div className="space-y-1.5">
                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Fractal Dimension:
                                    <Tooltip text="D_fractal from box-counting. Range: 2.0 (flat) to 3.0 (rugged)." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.transmissionPhysics.physicsParams?.fractalDimension}
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Shift Frequency:
                                    <Tooltip text="f_shift = k_terrain × D_fractal × v_avg. Shifts per hour." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.transmissionPhysics.physicsParams?.shiftFrequency_perHour} /hr
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Total Shifts:
                                    <Tooltip text="N_shifts = f_shift × duration_hours. Total gear shifts." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {terrainParameters.transmissionPhysics.physicsParams?.totalShifts}
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                    Torque Ratio:
                                    <Tooltip text="(T_transmission / T_rated). Percentage of rated capacity." />
                                  </span>
                                  <span className="text-indigo-200 font-mono text-xs">
                                    {(terrainParameters.transmissionPhysics.physicsParams?.torqueRatio * 100).toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteAnalysisCard;
